import { MATERIALS, type MaterialId } from './materials';

/** Chemical formula-unit amounts per one simulation biomass/particle mass unit.
 * These are artwork units, not kilograms or planetary gravitational masses. */
export const BIOLOGICAL_MATERIAL_SCALE = 0.00002;
export const NUTRIENT_UNITS_PER_BIOMASS = 0.014;
export const AMMONIA_PER_NUTRIENT = BIOLOGICAL_MATERIAL_SCALE * 0.02 / NUTRIENT_UNITS_PER_BIOMASS;
export const METABOLIC_BIOMASS_PER_ENERGY = 0.02;

export interface BiomassRecipe {
  readonly name: string;
  readonly materials: Readonly<Partial<Record<MaterialId, number>>>;
}

export const BIOMASS_RECIPES: readonly BiomassRecipe[] = [
  { name: 'No particulate recipe', materials: {} },
  { name: 'Aquatic carbon biomass', materials: {
    carbon: BIOLOGICAL_MATERIAL_SCALE,
    water: BIOLOGICAL_MATERIAL_SCALE,
    ammonia: BIOLOGICAL_MATERIAL_SCALE * 0.02,
  } },
  { name: 'Rocky debris', materials: { basalt: BIOLOGICAL_MATERIAL_SCALE * 0.8, iron: BIOLOGICAL_MATERIAL_SCALE * 0.2 } },
  { name: 'Icy comet carrier', materials: { water: BIOLOGICAL_MATERIAL_SCALE * 0.8, basalt: BIOLOGICAL_MATERIAL_SCALE * 0.2 } },
];
export const AQUATIC_RECIPE = 1;
/** Precompiled immutable recipes avoid allocating material maps per organism. */
export const BIOMASS_RECIPE_TERMS = BIOMASS_RECIPES.map(recipe =>
  Object.entries(recipe.materials) as [MaterialId, number][]);
const MATERIAL_ELEMENT_TERMS = Object.fromEntries(Object.entries(MATERIALS).map(([id, material]) =>
  [id, Object.entries(material.elements)])) as Record<MaterialId, [string, number][]>;
const RECIPE_ELEMENT_TERMS = BIOMASS_RECIPE_TERMS.map(terms => {
  const elements: Record<string, number> = {};
  for (const [material, amount] of terms) for (const [element, coefficient] of MATERIAL_ELEMENT_TERMS[material]) {
    elements[element] = (elements[element] ?? 0) + amount * coefficient;
  }
  return Object.entries(elements);
});

export function defaultRecipe(kind: number): number {
  return kind === 10 ? 2 : kind === 9 ? 3 : [1, 2, 3, 7, 8].includes(kind) ? AQUATIC_RECIPE : 0;
}

export type ElementAmounts = Record<string, number>;
export function addMaterialElements(output: ElementAmounts, material: MaterialId, amount: number): void {
  for (const [element, coefficient] of MATERIAL_ELEMENT_TERMS[material]) {
    output[element] = (output[element] ?? 0) + coefficient * amount;
  }
}
export function addRecipeElements(output: ElementAmounts, recipe: number, amount: number): void {
  for (const [element, coefficient] of RECIPE_ELEMENT_TERMS[recipe]!) {
    output[element] = (output[element] ?? 0) + coefficient * amount;
  }
}

/** Simulation-owned transfer interface; storage and contact solvers need not
 * import climate or vegetation implementations. */
export interface MatterTransfers {
  release(recipe: number, amount: number, x: number, y: number, bodyIndex?: number): void;
  assimilate(consumer: number, sourceRecipe: number, amount: number, desiredGain: number): number;
  metabolize(index: number, energy: number): void;
  discard(index: number, destination?: number | 'space' | 'removed' | 'invalid'): void;
  importEntity(index: number): void;
  importBiomass(recipe: number, amount: number): void;
}
